import { CURRENT_CURRENCY } from '../../../config/constants';
import {
  TRANSACTION_METHODS,
  TRANSACTION_METHODS_NAME_MAP,
} from '../../../data/types/transactions';
import {
  PaymentMethodTag,
  DeliveryMethodTag,
  OrderListItemStyled,
} from './shared_components.styled';
import {
  ORDER_STATUS,
  ORDER_STATUS_COLOR_MAP,
  ORDER_STATUS_NAME_MAP,
  OrderBrief,
  OrderDetails,
  OrderItemBrief,
  OrderAction,
  terminalStatuses,
} from '../../../data/types/orders';
import Flammable from '../../../assets/images/svg/Flammable';
import Fragile from '../../../assets/images/svg/Fragile';
import Liquid from '../../../assets/images/svg/Liquid';
import ShowImage from '../../../components/show_image';
import { Colors } from '../../../config/theme';
import {
  formatDecimals,
  formatPrice,
} from '../../../utils/functions/formatting_functions';
import { AttributeType } from '../../../data/types/attributes';
import { Collapse, Row, Tag, Timeline } from 'antd';
import { formateServerDateToLocaleDate } from '../../../utils/functions/time_date_functions';
import {
  DELIVERY_SHIPMENT_STATUS,
  DELIVERY_SHIPMENT_STATUS_COLOR_MAP,
  DELIVERY_SHIPMENT_STATUS_NAME_MAP,
  Shipment,
  ShipmentAction,
} from '../../../data/types/shipping';
import { Card, CardBody, CardHeader, Chip } from '@nextui-org/react';
import { Col } from 'antd';
import {
  PackageImageMap,
  determinePackageType,
} from '../../../pages/create_listings/constants';
import { IoCheckbox, IoCheckboxOutline } from 'react-icons/io5';
import { BsLock, BsUnlock } from 'react-icons/bs';
import { ImCheckboxUnchecked } from 'react-icons/im';

const OrderPaymentMethodTag = (payment_type: TRANSACTION_METHODS) => {
  return (
    <PaymentMethodTag>
      <span className={`p-2 rounded method method-${payment_type} lato`}>
        {TRANSACTION_METHODS_NAME_MAP[payment_type] as string}
      </span>
    </PaymentMethodTag>
  );
};

const OrderDeliveryMethodTag = (order: OrderBrief) => {
  return (
    <DeliveryMethodTag>
      <span
        className={`p-2 rounded method ${order.last_mile_shift_id ? 'delivery' : 'pickup'} lato`}
      >
        {order.last_mile_shift_id ? 'Delivery' : 'Pickup'}
      </span>
    </DeliveryMethodTag>
  );
};

const OrderStatusTag = (status: ORDER_STATUS, date?: string) => {
  return (
    <Tag
      color={
        ORDER_STATUS_COLOR_MAP[status as keyof typeof ORDER_STATUS_COLOR_MAP]
      }
      className="flex justify-center"
      style={{
        borderRadius: '5px',
        fontSize: '12px',
      }}
    >
      {ORDER_STATUS_NAME_MAP[status as keyof typeof ORDER_STATUS_NAME_MAP]}{' '}
      {date ? `@ ${date}` : ''}
    </Tag>
  );
};

const ShipmentStatusTag = ({
  status,
  shipment,
  date,
}: {
  status: DELIVERY_SHIPMENT_STATUS;
  shipment?: Shipment;
  date?: string;
}) => {
  return shipment ? (
    <Chip
      style={{
        backgroundColor: DELIVERY_SHIPMENT_STATUS_COLOR_MAP[shipment.status],
        color: 'white',
      }}
      size="sm"
    >
      {DELIVERY_SHIPMENT_STATUS_NAME_MAP[shipment.status]}
      {shipment.status === DELIVERY_SHIPMENT_STATUS.cancelled ||
      shipment.status === DELIVERY_SHIPMENT_STATUS.zero
        ? ''
        : shipment.status === DELIVERY_SHIPMENT_STATUS.failed_dropoff_collected
          ? ' at ' + shipment?.dropped_off_address?.owner_type
          : shipment.status === DELIVERY_SHIPMENT_STATUS.dropped_off ||
              shipment.status === DELIVERY_SHIPMENT_STATUS.failed_dropoff
            ? ' to ' + shipment.planned_destination_address.owner_type
            : ' from ' + shipment.source_address.owner_type}
    </Chip>
  ) : (
    <Tag
      color={
        DELIVERY_SHIPMENT_STATUS_COLOR_MAP[
          status as keyof typeof DELIVERY_SHIPMENT_STATUS_COLOR_MAP
        ]
      }
      style={{
        borderRadius: '5px',
        fontSize: '12px',
      }}
    >
      {
        DELIVERY_SHIPMENT_STATUS_NAME_MAP[
          status as keyof typeof DELIVERY_SHIPMENT_STATUS_NAME_MAP
        ]
      }{' '}
      {date ? `@ ${date}` : ''}
    </Tag>
  );
};

const OrderListItem = ({ orderItem }: { orderItem: OrderItemBrief }) => {
  const { item, unit_price, quantity } = orderItem;
  return (
    <OrderListItemStyled>
      <div className="d-flex align-items-center">
        <div className="item-width d-flex align-items-center">
          <ShowImage
            image={item.image}
            style={{
              width: '65px',
              height: '65px',
            }}
          />
          <h6 className="pr-2 lato">
            {item.name_en}
            <br />
            {item.name_ar}
          </h6>{' '}
          {/* TODO localize */}
        </div>{' '}
        <div className="col-width">
          <h6>#{item.id}</h6>
        </div>
        <div className="col-width">
          <h6>{quantity}</h6>
        </div>
        <div className="col-width">
          <h6>{formatPrice(unit_price)}</h6>
        </div>
        <div className="col-width right">
          <h6>
            <b>
              {CURRENT_CURRENCY} {quantity * parseFloat(unit_price)}
            </b>
          </h6>
        </div>
      </div>
      {/* Order Items customization */}
      {item.attributes && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {item.attributes &&
            item.attributes.map((attr, index) => {
              return (
                <div className="customization" key={index}>
                  <div
                    className=" d-flex align-items-start"
                    style={{
                      padding: '5px 10px',
                      backgroundColor: Colors.greenSecondary,
                      borderRadius: '5px',
                      visibility: index === 0 ? 'visible' : 'hidden',
                    }}
                  >
                    <h5 className="lato" style={{ color: Colors.greenMain }}>
                      Customizations
                    </h5>
                  </div>

                  <div className="item-width d-flex align-items-start">
                    <h5 className="lato" style={{ color: '#4c6870' }}>
                      {attr.attr_name_en + ' | ' + attr.attr_name_ar}
                    </h5>
                  </div>
                  <div className="col-width" style={{ fontWeight: 'bold' }}>
                    <h5>
                      {attr.attribute_type === AttributeType.NUMERIC
                        ? formatDecimals(attr.attr_value_en.toString())
                        : attr.attr_value_en}
                    </h5>
                  </div>
                </div>
              );
            })}
          {/* {item.attributes.map((attribute, index) => (
            <div key={index} className="d-flex align-items-center">
              <div className="item-width d-flex align-items-center">
                <h6 className="pr-2 lato">{attribute.attr_name}</h6>
              </div>
              <div className="col-width">
                <h6>{attribute.attr_value}</h6>
              </div>
            </div>
          ))} */}
        </div>
      )}
      {/* Order Notes */}
      {orderItem.seller_notes && (
        <div className={'note-active lato'}>
          Note: <span>{orderItem.seller_notes}</span>
        </div>
      )}
    </OrderListItemStyled>
  );
};

const OrderTimeline = ({
  headerStyle,
  customHeader,
  orderActions,
}: {
  headerStyle?: React.CSSProperties;
  customHeader?: React.ReactNode;
  orderActions: OrderAction[];
}) => {
  const color = (status: number) => {
    if (status >= 0 && status < 24) {
      return 'green';
    }
    if (status >= 24 && status < 40) {
      return 'purple';
    }
    if (status >= 40 && status < 60) {
      return 'red';
    }
  };

  return (
    <>
      {customHeader ?? <h6 style={headerStyle}>Order Timeline</h6>}
      <Timeline
        className="mt-3"
        style={{ width: '100%' }}
        pending={
          !terminalStatuses.has(orderActions[orderActions.length - 1].action)
        }
        items={orderActions.map((action) => {
          return {
            children: (
              <Row>
                {OrderStatusTag(
                  action.action,
                  formateServerDateToLocaleDate(action.action_date),
                )}
              </Row>
            ),
            color: color(action.action),
            style: {
              borderRadius: '5px',
              fontSize: '14px',
            },
          };
        })}
      />
    </>
  );
};

const ShipmentTimeline = ({
  shipmentsActions,
}: {
  shipmentsActions: ShipmentAction[];
}) => {
  const terminalStatuses = new Set([
    DELIVERY_SHIPMENT_STATUS.cancelled,
    DELIVERY_SHIPMENT_STATUS.dropped_off,
  ]);
  return (
    <Timeline
      className="mt-3"
      style={{ width: '100%' }}
      // pending={
      //   shipmentsActions.length !== 0 &&
      //   !terminalStatuses.has(
      //     shipmentsActions[shipmentsActions.length - 1]?.status_after_action,
      //   )
      // }
      items={shipmentsActions.map((action) => {
        return {
          children: (
            <Row>
              <ShipmentStatusTag
                status={action.status_after_action}
                date={formateServerDateToLocaleDate(action.action_date)}
              />
            </Row>
          ),
          color: 'Red',
          style: {
            borderRadius: '5px',
            fontSize: '14px',
          },
        };
      })}
    />
  );
};

export const OrderPackageQualities = (orderDetail: OrderDetails) => {
  const relevantShipments =
    orderDetail.shipments[orderDetail.shipments.length - 1] ?? null;
  const PACKAGE_QUALITIES_SIZE = 40;

  function hasFragileItem(orderDetail: OrderDetails) {
    return orderDetail.items.some((item) => item.listing.fragile === true);
  }
  function hasFlammableItem(orderDetail: OrderDetails) {
    return orderDetail.items.some((item) => item.listing.flammable === true);
  }
  function hasKeepDryItem(orderDetail: OrderDetails) {
    return orderDetail.items.some((item) => item.listing.keep_dry === true);
  }

  const flammable = () => {
    return (
      <Flammable
        width={PACKAGE_QUALITIES_SIZE}
        height={PACKAGE_QUALITIES_SIZE}
      />
    );
  };

  const fragile = () => {
    return (
      <Fragile width={PACKAGE_QUALITIES_SIZE} height={PACKAGE_QUALITIES_SIZE} />
    );
  };

  const keepDry = () => {
    return (
      <Liquid width={PACKAGE_QUALITIES_SIZE} height={PACKAGE_QUALITIES_SIZE} />
    );
  };

  return (
    <Card
      style={{
        borderRadius: '8px',
        backgroundColor: Colors.white,
        minHeight: '50vh',
        maxHeight: '50vh',
        overflow: 'scroll',
      }}
      shadow="sm"
    >
      <CardHeader className="justify-between pb-0 align-top">
        Order Shipments{' '}
        {orderDetail.shipments.some(
          (shipment) => shipment.requires_security_code,
        ) ? (
          <Chip
            startContent={<BsLock color={Colors.blueMain} size={20} />}
            color="warning"
            variant="solid"
          >
            {orderDetail.security_code}
          </Chip>
        ) : (
          <Chip
            color="warning"
            variant="dot"
            startContent={<BsUnlock color={Colors.blueMain} size={20} />}
          >
            No code
          </Chip>
        )}
        <div className="flex mt-1">
          {hasFragileItem(orderDetail) && fragile()}
          {hasFlammableItem(orderDetail) && flammable()}
          {hasKeepDryItem(orderDetail) && keepDry()}
        </div>
      </CardHeader>
      <CardBody> {PackageLabels(orderDetail)}</CardBody>
    </Card>
  );
};

export const PackageLabels = (orderDetails: OrderDetails) => {
  const panelStyle: React.CSSProperties = {
    marginBottom: 12,
    background: '#f7f7f7',
    borderRadius: '8px',
    border: 'none',
  };

  const getShipmentItems = () =>
    orderDetails.shipments.toReversed().map((shipment, i) => ({
      key: i.toString(),
      label: (
        <p
          style={{
            color: Colors.primaryText,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          Shipment #{shipment.id}{' '}
          <ShipmentStatusTag status={shipment.status} shipment={shipment} />
        </p>
      ),
      children: PackageDimensionsAndTracking(shipment),
      style: panelStyle,
    }));

  return (
    <>
      {orderDetails.shipments.length > 0 ? (
        <Collapse ghost defaultActiveKey={['0']} items={getShipmentItems()} />
      ) : (
        <label>No packages yet</label>
      )}
    </>
  );
};

export function PackageDimensionsAndTracking(shipment: Shipment) {
  return shipment.packages.map((pkg, index) => {
    const relevantPackageSizeName = determinePackageType(
      pkg.width,
      pkg.height,
      pkg.length,
    );
    const checkboxColor =
      shipment.status === DELIVERY_SHIPMENT_STATUS.picked_up ||
      shipment.status === DELIVERY_SHIPMENT_STATUS.dropped_off
        ? Colors.greenMain
        : shipment.status === DELIVERY_SHIPMENT_STATUS.failed_dropoff ||
            shipment.status === DELIVERY_SHIPMENT_STATUS.failed_pickup
          ? Colors.tomatoRed
          : Colors.grayLight;

    return (
      <div
        key={index}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'white',
          padding: '7px 8px',
          borderRadius: '5px',
          margin: '5px 0',
          paddingTop: '10px',
        }}
      >
        <Col>
          {shipment.status === DELIVERY_SHIPMENT_STATUS.picked_up ? (
            <IoCheckboxOutline color={Colors.greenMain} size={30} />
          ) : shipment.status === DELIVERY_SHIPMENT_STATUS.dropped_off ? (
            <IoCheckbox color={Colors.greenMain} size={30} />
          ) : (
            <ImCheckboxUnchecked color={checkboxColor} size={30} />
          )}
        </Col>
        <Col
          style={{
            fontSize: '14px',
          }}
        >
          #{pkg.tracking_code}
        </Col>
        <Col
          style={{
            alignItems: 'center',
          }}
        >
          {PackageImageMap(relevantPackageSizeName.name, 22, 22, false)}
          <span className="detail d-flex justify-end  dimensions">
            {relevantPackageSizeName.name}
          </span>
        </Col>
        <Col>
          <span className="detail d-flex justify-end mx-1 dimensions">
            {formatDecimals(pkg.weight.toString())}g
          </span>
          <span className="detail d-flex justify-content-between mx-1 dimensions">
            {formatDecimals(pkg.width.toString())}x
            {formatDecimals(pkg.height.toString())}x
            {formatDecimals(pkg.length.toString())}cm
          </span>
        </Col>
      </div>
    );
  });
}

export {
  OrderPaymentMethodTag,
  OrderDeliveryMethodTag,
  OrderStatusTag,
  OrderListItem,
  OrderTimeline,
  ShipmentStatusTag,
  ShipmentTimeline,
};
